/**
 * useRcaFilters — the Agents console's ticket filters, kept in the URL (TRUS-687).
 *
 * Why not react-router's useSearchParams: the console also mounts outside a router
 * (tests, the Triveny shell), and the app shell's own popstate handler restores the
 * active tab from `history.state.tab`. A react-router push replaces history.state with
 * its own `{usr, key, idx}`, so Back would drop the user off the Agents tab. We write
 * the URL ourselves and carry the shell's state along, which keeps Back/Forward on the
 * Agents tab and walking through filter changes.
 *
 * Only ids and enum values reach the URL (see rcaFilterModel). The search text `q`
 * is component state only: it can hold anything a person types.
 *
 * `?job=` (agentJobLink) is untouched: we never write or read it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EXCLUSIVE_AXES,
  RCA_URL_KEYS,
  activeFilterCount,
  axisParams,
  clearedAxes,
  parseRcaUrl,
  sameFilterState,
  stateFromViewParams,
  viewParamsFrom,
  writeRcaUrl,
  type RcaAxis,
  type RcaFilterState,
  type RcaGroupBy,
  type RcaSort,
  type RcaViewParams,
} from './rcaFilterModel';

// Mounted instances (module scope): the console remounts while auth settles, and its
// filters must survive that. On the last unmount — a real tab switch — the owned keys are
// stripped from the address bar so they don't trail into the next tab's URL.
let mountedConsoles = 0;

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

function writeUrl(state: RcaFilterState, mode: 'push' | 'replace') {
  if (typeof window === 'undefined') return;
  const sp = writeRcaUrl(state, currentSearch());
  const qs = sp.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  if (next === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;
  const st = window.history.state;
  // Push only when the shell's tab is in the state we carry forward; without it the shell
  // would treat Back as "leave this tab", so a replace is the safe choice.
  const canPush = mode === 'push' && st && typeof st === 'object' && 'tab' in st;
  try {
    if (canPush) window.history.pushState({ ...st }, '', next);
    else window.history.replaceState(st, '', next);
  } catch {
    /* a sandboxed frame may refuse; state still updates in memory */
  }
}

function stripOwnedKeys() {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (!RCA_URL_KEYS.some((k) => sp.has(k))) return;
  for (const k of RCA_URL_KEYS) sp.delete(k);
  const qs = sp.toString();
  try {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  } catch {
    /* ignore */
  }
}

export interface UseRcaFilters {
  state: RcaFilterState;
  /** Toggle one value on an axis (OR within the axis). */
  toggle: (axis: RcaAxis, value: string) => void;
  /** Replace an axis's values. */
  setAxis: (axis: RcaAxis, values: string[], opts?: { replace?: boolean }) => void;
  /** Clear every axis (and the search). Sort is kept; grouping too unless `group` is given. */
  clearAll: (opts?: { replace?: boolean; group?: RcaGroupBy | '' }) => void;
  setSort: (sort: RcaSort) => void;
  setGroup: (group: RcaGroupBy | '') => void;
  /** Apply a saved view's params (a full state + its q). */
  applyView: (params: RcaViewParams) => void;
  /** The current state as saved-view params. */
  viewParams: () => RcaViewParams;
  q: string;
  setQ: (q: string) => void;
  /** `q` after a 300ms debounce, trimmed: what the server is asked for. */
  debouncedQ: string;
  /** The axis params for `/agents/rca/log` (comma-joined, ids/enums only). */
  params: ReturnType<typeof axisParams>;
  activeCount: number;
  /** Any axis filtered, or a search typed. */
  filtersActive: boolean;
}

export function useRcaFilters(): UseRcaFilters {
  const [state, setState] = useState<RcaFilterState>(() => parseRcaUrl(currentSearch()));
  const stateRef = useRef(state);
  stateRef.current = state;
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => window.clearTimeout(id);
  }, [q]);

  // Back/Forward: re-read the URL (never write it here).
  useEffect(() => {
    const onPop = () => {
      const next = parseRcaUrl(currentSearch());
      if (!sameFilterState(next, stateRef.current)) setState(next);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    mountedConsoles += 1;
    return () => {
      mountedConsoles -= 1;
      window.setTimeout(() => {
        if (mountedConsoles === 0) stripOwnedKeys();
      }, 0);
    };
  }, []);

  const commit = useCallback((next: RcaFilterState, mode: 'push' | 'replace' = 'push') => {
    if (sameFilterState(next, stateRef.current)) return;
    stateRef.current = next;
    setState(next);
    writeUrl(next, mode);
  }, []);

  const setAxis = useCallback<UseRcaFilters['setAxis']>(
    (axis, values, opts) => {
      const cur = stateRef.current;
      const vals = EXCLUSIVE_AXES.has(axis) ? values.slice(0, 1) : values;
      commit({ ...cur, axes: { ...cur.axes, [axis]: vals } }, opts?.replace ? 'replace' : 'push');
    },
    [commit],
  );

  const toggle = useCallback<UseRcaFilters['toggle']>(
    (axis, value) => {
      const cur = stateRef.current.axes[axis] ?? [];
      if (cur.includes(value)) setAxis(axis, cur.filter((v) => v !== value));
      else setAxis(axis, EXCLUSIVE_AXES.has(axis) ? [value] : [...cur, value]);
    },
    [setAxis],
  );

  const clearAll = useCallback<UseRcaFilters['clearAll']>(
    (opts) => {
      setQ('');
      setDebouncedQ('');
      const cur = stateRef.current;
      commit({ ...cur, axes: clearedAxes(), group: opts?.group ?? cur.group }, opts?.replace ? 'replace' : 'push');
    },
    [commit],
  );

  const setSort = useCallback((sort: RcaSort) => commit({ ...stateRef.current, sort }, 'replace'), [commit]);
  const setGroup = useCallback((group: RcaGroupBy | '') => commit({ ...stateRef.current, group }), [commit]);

  const applyView = useCallback(
    (params: RcaViewParams) => {
      const { state: next, q: vq } = stateFromViewParams(params, stateRef.current);
      setQ(vq);
      setDebouncedQ(vq.trim());
      commit(next);
    },
    [commit],
  );

  const qRef = useRef(q);
  qRef.current = q;
  const viewParams = useCallback(() => viewParamsFrom(stateRef.current, qRef.current), []);

  const params = useMemo(() => axisParams(state), [state]);
  const activeCount = activeFilterCount(state);
  return {
    state,
    toggle,
    setAxis,
    clearAll,
    setSort,
    setGroup,
    applyView,
    viewParams,
    q,
    setQ,
    debouncedQ,
    params,
    activeCount,
    filtersActive: activeCount > 0 || q.trim() !== '',
  };
}
